import type { Platform } from "./domain";

export type Grouping = "day" | "week" | "month";

// Every reporting aggregate carries one of these so a consumer never has to
// guess whether a blank means "zero" or "nobody measured it". They are kept
// distinct on purpose — collapsing any pair back into a number is the bug this
// contract exists to prevent.
//   measured     — every required day was measured and the sum is positive
//   zero         — every required day was measured and the sum is exactly zero
//   unmeasured   — no required day was measured; the value is absent, not zero
//   incomplete   — some required days were measured and at least one was not,
//                  so returning the partial sum would understate the range
//   unconfigured — the metric/conversion has no configuration to measure at all
//   provisional  — measured but inside a lagging window, so it may still change
export type MeasurementState =
  | "measured"
  | "zero"
  | "unmeasured"
  | "incomplete"
  | "unconfigured"
  | "provisional";

// A reporting-derived delivery signal for one breakdown entity, computed from
// the daily facts already in range rather than a live provider status read
// (the provider-effective Delivery status contract is issue 11's
// `getEntityDeliveryStatuses`, wired into creative detail by issue 13).
//   delivering — the entity had impressions on the latest completed reporting
//                day present in the range
//   inactive   — it delivered earlier in the range but not on that latest day
//   none       — it had no measured impressions anywhere in the range
export type DeliveryState = "delivering" | "inactive" | "none";

export interface TimeBucket {
  key: string; // '2026-03-01', '2026-W10', 'Mar'
  label: string; // 'Mar 1', 'W10', 'Mar'
  start: string; // ISO date of bucket start
  end: string; // ISO date of bucket end
}

export interface MetricRow {
  /** Optional server-resolved names used by stored-report coverage checks. */
  measurementNames?: string[];
  // Inline classification heading; never the saved Breakdown's configuration name.
  group?: { id: string; label: string; comparisonOnly?: boolean };
  id: string; // e.g. 'meta:spend', 'meta:new_customers', 'meta:cac'
  label: string; // e.g. 'Spend', 'New Customers', 'CAC'
  type: "currency" | "number" | "currency_per"; // formatting hint
  // null means "no data for this bucket" (as opposed to a real zero) — CPA is
  // also null when its bucket has zero conversions, since spend/0 reads as
  // "free" rather than "no conversions yet".
  values: Record<string, number | null>; // keyed by TimeBucket.key
  total: number | null;
  deltas?: Record<string, number>; // % change vs comparison, keyed by TimeBucket.key
  deltaTotal?: number; // % change for total
  absoluteDeltas?: Record<string, number>; // absolute change values
  absoluteDeltaTotal?: number;
  invertColor?: boolean; // true for cost metrics (lower = green)
  // Measurement state of the row's total across the whole range, and of each
  // individual bucket. `total`/`values` stay numeric for backward compatibility;
  // these fields name what that number (or null) actually means — in particular
  // an `incomplete` total is a range where a required day went unmeasured and
  // must not be read as a real sum.
  measurementState?: MeasurementState;
  states?: Record<string, MeasurementState>; // keyed by TimeBucket.key
  // Why a delta is hidden: its own figures were not measured in one of the two
  // periods, or the periods' buckets do not line up. Keyed by TimeBucket.key;
  // the grid shows "–" with the reason, exports a blank.
  deltaGaps?: Record<string, DeltaGap>;
  deltaTotalGap?: DeltaGap;
}

export interface DeltaGap {
  kind: "history" | "unmeasured" | "conversion" | "currency" | "unaligned";
  // Which period the gap is in.
  period: "comparison" | "current";
  platform?: Platform;
  conversion?: string;
  days?: number;
  // First stored day when history starts inside the period.
  since?: string;
  reason: string;
}

export interface PlatformSection {
  platform: Platform | "total";
  label: string; // 'Meta', 'Google', 'TikTok', 'Total'
  rows: MetricRow[];
  // Reporting-semantics annotations computed server-side (see
  // grid-aggregation.ts buildFreshnessAnnotations) so every consumer — grid,
  // CSV, future MCP — sees the same interpretation instead of each recomputing
  // it from platformHealth.
  understated?: boolean;
  understatedReason?: string;
  // Only set on the "total" section: which contributing platforms were
  // stale/missing and are therefore silently under-counted in this total.
  excludedPlatforms?: Platform[];
}

export interface PlatformHealth {
  platform: Platform;
  latestDate: string | null;
  rowsInRange: number;
  rowsWithConversionsInRange: number;
  stale: boolean;
  missingInRange: boolean;
  // Reporting days inside the range that no account of the platform has
  // published yet, and how many of those an import is currently working on.
  // A gap in the middle of a range is neither "stale" nor "no rows", so it
  // needs its own word.
  missingDays?: number;
  importingDays?: number;
}

// Manual data (Media Performance issue 16). Workspace-owned Custom metric
// values a person enters per Reporting day, rendered as their own group
// immediately below platform Total and NEVER summed into it. Units decide how
// a value is formatted, not what it means to the aggregation.
//   count      — a plain integer tally
//   currency   — a workspace-currency amount (no per-row currency conversion)
//   decimal    — a real number kept at its entered precision
//   percentage — a number rendered with a trailing % (stored as the number)
export type CustomMetricUnit = "count" | "currency" | "decimal" | "percentage";

// One Manual data cell for one bucket. `value` is null whenever the bucket is
// not a clean measured sum (unmeasured or incomplete) — exactly like a
// platform MetricRow, a null here is "no number to show", never a real zero.
// `editable`/`day` are only set when the bucket is a single Reporting day, so
// inline entry (daily-only for now) targets one unambiguous date.
export interface ManualDataCell {
  comparisonValue?: number | null;
  value: number | null;
  state: MeasurementState;
  editable: boolean;
  day: string | null;
}

export interface ManualDataRow {
  // The Custom metric id, stringified so the client key space stays uniform.
  metricId: string;
  label: string;
  unit: CustomMetricUnit;
  cells: Record<string, ManualDataCell>; // keyed by TimeBucket.key
  total: { value: number | null; state: MeasurementState; comparisonValue?: number | null };
}

export interface ManualDataGroup {
  rows: ManualDataRow[];
}

export interface GridData {
  // Independent classifications repeat nonzero source spend in a displayed period.
  breakdownsRepeatSpend?: boolean;
  sections: PlatformSection[];
  buckets: TimeBucket[];
  grouping: Grouping;
  dateRange: { start: string; end: string };
  platformHealth: PlatformHealth[];
  // One org = one currency for now; per-account currency is a future item
  // (see mergeTotals in grid-aggregation.ts, which sums raw numbers on that
  // assumption).
  currency: string;
  // The Manual data group, when the workspace has any active Custom metric.
  // Kept separate from `sections` so it can never be folded into a platform
  // Total; absent (undefined) when no Custom metric is configured.
  manualData?: ManualDataGroup;
}

// Inclusive list of ISO calendar days between two ISO dates. Small and pure so
// the Manual data builder can decide, per bucket, how many Reporting days it
// must cover before a partial entry counts as `incomplete` rather than a sum.
function enumerateDays(start: string, end: string): string[] {
  const days: string[] = [];
  const cursor = new Date(start + "T00:00:00Z");
  const last = new Date(end + "T00:00:00Z");
  while (cursor.getTime() <= last.getTime()) {
    days.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return days;
}

// Resolve the measured value and state for one metric over one set of Reporting
// days. The whole point of the measurement-state contract: a blank day is
// `unmeasured` (absent, not zero), an explicit entry of 0 across a fully
// covered span is `zero`, a mix of entered and blank days is `incomplete`
// (returning the partial sum would understate the span), and a fully covered
// span with a non-zero sum is `measured`.
function resolveManualSpan(
  requiredDays: string[],
  enteredByDay: Map<string, number>,
): { value: number | null; state: MeasurementState } {
  if (requiredDays.length === 0) return { value: null, state: "unmeasured" };
  let measuredCount = 0;
  let sum = 0;
  for (const day of requiredDays) {
    if (enteredByDay.has(day)) {
      measuredCount += 1;
      sum += enteredByDay.get(day)!;
    }
  }
  return resolveManualAggregate(requiredDays.length, measuredCount, sum);
}

// SQL readers can supply compact counts/sums without changing Manual data semantics.
export function resolveManualAggregate(requiredDays: number, measuredCount: number, sum: number): { value: number | null; state: MeasurementState } {
  if (requiredDays === 0 || measuredCount === 0) return { value: null, state: "unmeasured" };
  if (measuredCount < requiredDays) return { value: null, state: "incomplete" };
  return { value: sum, state: sum === 0 ? "zero" : "measured" };
}

// Build the Manual data group for the performance grid. Pure so the exact same
// grouping/measurement rules can be unit-tested without a database. Every
// active Custom metric always yields a row (a configured metric that was never
// entered still renders, as `unmeasured` dashes — absence must never look like
// a removed metric). Values are summed within a bucket's Reporting days; a
// bucket that spans exactly one day is `editable`, carrying that day so inline
// entry targets it.
export function buildManualDataGroup(args: {
  metrics: ReadonlyArray<{ id: string; name: string; unit: CustomMetricUnit }>;
  values: ReadonlyArray<{ metricId: string; day: string; value: number }>;
  buckets: readonly TimeBucket[];
  comparisonBuckets?: readonly TimeBucket[];
}): ManualDataGroup {
  const byMetric = new Map<string, Map<string, number>>();
  for (const entry of args.values) {
    let days = byMetric.get(entry.metricId);
    if (!days) {
      days = new Map<string, number>();
      byMetric.set(entry.metricId, days);
    }
    // One value per metric per Reporting day is a DB uniqueness guarantee; if a
    // caller passes duplicates the last write wins, matching an upsert.
    days.set(entry.day, entry.value);
  }
  const bucketDays = args.buckets.map((bucket) => enumerateDays(bucket.start, bucket.end));
  const allDays = [...new Set(bucketDays.flat())].sort();
  const comparisonDays = args.comparisonBuckets?.map((bucket) => enumerateDays(bucket.start, bucket.end));
  const aligned = comparisonDays?.length === bucketDays.length;
  const allComparisonDays = [...new Set(comparisonDays?.flat() ?? [])].sort();

  const rows: ManualDataRow[] = args.metrics.map((metric) => {
    const enteredByDay = byMetric.get(metric.id) ?? new Map<string, number>();
    const cells: Record<string, ManualDataCell> = {};
    args.buckets.forEach((bucket, index) => {
      const days = bucketDays[index]!;
      const span = resolveManualSpan(days, enteredByDay);
      const singleDay = bucket.start === bucket.end;
      cells[bucket.key] = {
        value: span.value,
        state: span.state,
        comparisonValue: aligned
          ? resolveManualSpan(comparisonDays![index]!, enteredByDay).value
          : null,
        editable: singleDay,
        day: singleDay ? bucket.start : null,
      };
    });
    return {
      metricId: metric.id,
      label: metric.name,
      unit: metric.unit,
      cells,
      total: {
        ...resolveManualSpan(allDays, enteredByDay),
        comparisonValue: aligned
          ? resolveManualSpan(allComparisonDays, enteredByDay).value
          : null,
      },
    };
  });

  return { rows };
}

/**
 * Determine auto-grouping from the number of days in the range: up to a week
 * reads daily, up to five weeks weekly, longer monthly. Days are counted
 * between UTC midnights so a range across a daylight-saving change (a
 * 23- or 25-hour day) keeps its true length: Last 7 days stays Daily.
 */
export function autoGrouping(start: string, end: string): Grouping {
  const startDate = Date.parse(`${start}T00:00:00Z`);
  const endDate = Date.parse(`${end}T00:00:00Z`);
  const days = Math.round((endDate - startDate) / 86_400_000) + 1;
  if (days <= 7) return "day";
  if (days <= 35) return "week";
  return "month";
}
